import C from "@/components/ActionMenu.module.css";

import { WPopover } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import type { ToastifyData } from "@/modules/notifications/toast.tsx";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { GridList, GridListItem, Header, MenuTrigger } from "react-aria-components";
import type { TypeOptions } from "react-toastify";
import { useNotificationCenter, type UseNotificationCenter } from "react-toastify/addons/use-notification-center";

const typeIcons: Partial<Record<TypeOptions, string>> = {
    success: "jp-icon-checkmark color-success",
    error: "jp-wrong-icon color-error",
};

export interface NotificationItemProps {
    type?: TypeOptions;
    icon?: string | undefined;
    nc: UseNotificationCenter<ToastifyData>;
    id: number | string;
    children: ReactNode;
    fallbackText?: string;
}

export function NotificationItem({ type, icon, nc, id, children }: NotificationItemProps) {
    // Use type icon as fallback if no icon is provided
    icon ??= type ? typeIcons[type] : undefined;

    const iconElement = icon ? <MaskIcon className={cn(icon, "size-1rem")} /> : null;

    return (
        <GridListItem
            className={cn(
                C.ActionMenuItem,
                C.NotificationMenuItem,
                type && `Toastify__toast--${type}`,
                type === "success" && C.success,
            )}
            key={id}
        >
            {iconElement && <div className="Toastify__toast-icon">{iconElement}</div>}
            <div className={C.title}>{children}</div>
            <WButton
                className={C.button}
                variant="ghost"
                size="xs"
                icon="jp-icon-close"
                ariaLabel="Remove"
                action={() => nc.remove(id)}
            />
        </GridListItem>
    );
}

export const NotificationButton = ({ hasUnread }: { hasUnread: boolean }) => {
    return (
        <div className={hasUnread ? cn(C.hasUnread, "text-primary") : undefined}>
            <WButton
                color="primary"
                variant="ghost"
                size="bar"
                icon="jp-bell-icon"
                ariaLabel="Notifications"
                action={undefined}
            />
        </div>
    );
};

export default function NotificationMenu() {
    const nc = useNotificationCenter<ToastifyData>();

    const notifications = nc.notifications.filter((n) => !n.data?.noNotification);

    const items = notifications.map((n) => {
        const { type, data, id, content } = n;
        return (
            <NotificationItem key={id} id={id} type={type} icon={data?.icon} nc={nc}>
                {content as ReactNode}
            </NotificationItem>
        );
    });

    const hasUnread = notifications.some((n) => !n.read);

    return (
        <MenuTrigger>
            <NotificationButton hasUnread={hasUnread} />

            <WPopover className={C.NotificationMenu} placement="bottom">
                <Header className={C.ActionMenuHeader}>
                    <Trans>Notifications</Trans>
                    {!!items.length && (
                        <WButton color="primary" variant="ghost" size="xs" action={() => nc.clear()}>
                            <Trans>Clear all</Trans>
                        </WButton>
                    )}
                </Header>

                <GridList className={C.Scrollable} selectionMode="none">
                    {items.length === 0 ? (
                        <GridListItem className={C.ActionMenuItem} isDisabled={true}>
                            <Trans>You have no notifications.</Trans>
                        </GridListItem>
                    ) : (
                        items
                    )}
                </GridList>
            </WPopover>
        </MenuTrigger>
    );
}
